import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BudgetDto,
  BudgetPatchDto,
  BudgetsResponseDto,
  CategoryCreateDto,
  CategoryDto,
  CategoryPatchDto,
  OverviewDto,
  PaymentCreateDto,
  PaymentDto,
  PaymentPaidPatchDto,
  PaymentPaidResultDto,
  PaymentPatchDto,
  PaymentsResponseDto,
  RateDto,
  SettingsDto,
  SettingsPatchDto,
  SubscriptionCreateDto,
  SubscriptionDto,
  SubscriptionPatchDto,
  SubscriptionsResponseDto,
  TransactionCreateDto,
  TransactionDto,
  TransactionPatchDto,
} from '@trestly/shared';
import { api, ApiError, type BudgetUpsertBody } from './client';

/** Ключи кеша. Списками, чтобы инвалидация по префиксу цепляла все периоды. */
export const queryKeys = {
  overview: (period: string) => ['overview', period] as const,
  overviewAll: () => ['overview'] as const,
  settings: () => ['settings'] as const,
  subscriptions: () => ['subscriptions'] as const,
  payments: (period: string) => ['payments', period] as const,
  paymentsAll: () => ['payments'] as const,
  categories: (includeArchived?: boolean) =>
    ['categories', includeArchived ?? false] as const,
  /** Префикс для инвалидации всех вариантов (активных и архивных). */
  categoriesAll: () => ['categories'] as const,
  budgets: (period: string) => ['budgets', period] as const,
  budgetsAll: () => ['budgets'] as const,
  transactions: (period: string, categoryId?: number) =>
    ['transactions', period, categoryId ?? 'all'] as const,
  transactionsAll: () => ['transactions'] as const,
  rate: (on: string) => ['rate', on] as const,
};

/**
 * Инвалидация после изменения фактических трат: сумма влияет на остаток
 * категории (budgets), сводку главной (overview) и сам список транзакций.
 */
function invalidateSpending(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
  void qc.invalidateQueries({ queryKey: queryKeys.budgetsAll() });
  void qc.invalidateQueries({ queryKey: queryKeys.transactionsAll() });
}

/* ------------------------------- overview ------------------------------- */

export function useOverview(period: string): UseQueryResult<OverviewDto, ApiError> {
  return useQuery<OverviewDto, ApiError>({
    queryKey: queryKeys.overview(period),
    queryFn: () => api.overview(period),
  });
}

/* ------------------------------- settings ------------------------------- */

export function useSettings(): UseQueryResult<SettingsDto, ApiError> {
  return useQuery<SettingsDto, ApiError>({
    queryKey: queryKeys.settings(),
    queryFn: () => api.settings.get(),
  });
}

export function useUpdateSettings(): UseMutationResult<SettingsDto, ApiError, SettingsPatchDto> {
  const qc = useQueryClient();
  return useMutation<SettingsDto, ApiError, SettingsPatchDto>({
    mutationFn: (body) => api.settings.patch(body),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings(), data);
      // Смена базовой валюты меняет ВСЕ приведённые суммы — сбрасываем всё.
      void qc.invalidateQueries();
    },
  });
}

/* ----------------------------- subscriptions ---------------------------- */

export function useSubscriptions(): UseQueryResult<SubscriptionsResponseDto, ApiError> {
  return useQuery<SubscriptionsResponseDto, ApiError>({
    queryKey: queryKeys.subscriptions(),
    queryFn: () => api.subscriptions.list(),
  });
}

function invalidateSubscriptions(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.subscriptions() });
  // На главной есть карточка «Подписки» с итогом.
  void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
}

export function useCreateSubscription(): UseMutationResult<
  SubscriptionDto,
  ApiError,
  SubscriptionCreateDto
> {
  const qc = useQueryClient();
  return useMutation<SubscriptionDto, ApiError, SubscriptionCreateDto>({
    mutationFn: (body) => api.subscriptions.create(body),
    onSuccess: () => invalidateSubscriptions(qc),
  });
}

export function useUpdateSubscription(): UseMutationResult<
  SubscriptionDto,
  ApiError,
  { id: number; patch: SubscriptionPatchDto }
> {
  const qc = useQueryClient();
  return useMutation<SubscriptionDto, ApiError, { id: number; patch: SubscriptionPatchDto }>({
    mutationFn: ({ id, patch }) => api.subscriptions.patch(id, patch),
    onSuccess: () => invalidateSubscriptions(qc),
  });
}

export function useDeleteSubscription(): UseMutationResult<void, ApiError, number> {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api.subscriptions.remove(id),
    onSuccess: () => invalidateSubscriptions(qc),
  });
}

/* -------------------------------- payments ------------------------------ */

export function usePayments(period: string): UseQueryResult<PaymentsResponseDto, ApiError> {
  return useQuery<PaymentsResponseDto, ApiError>({
    queryKey: queryKeys.payments(period),
    queryFn: () => api.payments.list(period),
  });
}

function invalidatePayments(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.paymentsAll() });
  void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
}

export function useCreatePayment(): UseMutationResult<PaymentDto, ApiError, PaymentCreateDto> {
  const qc = useQueryClient();
  return useMutation<PaymentDto, ApiError, PaymentCreateDto>({
    mutationFn: (body) => api.payments.create(body),
    onSuccess: () => invalidatePayments(qc),
  });
}

export function useUpdatePayment(): UseMutationResult<
  PaymentDto,
  ApiError,
  { id: number; patch: PaymentPatchDto }
> {
  const qc = useQueryClient();
  return useMutation<PaymentDto, ApiError, { id: number; patch: PaymentPatchDto }>({
    mutationFn: ({ id, patch }) => api.payments.patch(id, patch),
    onSuccess: () => invalidatePayments(qc),
  });
}

export function useSetPaymentPaid(
  period: string,
): UseMutationResult<PaymentPaidResultDto, ApiError, { id: number } & PaymentPaidPatchDto> {
  const qc = useQueryClient();
  return useMutation<PaymentPaidResultDto, ApiError, { id: number } & PaymentPaidPatchDto>({
    mutationFn: ({ id, paid, amountMinorActual }) =>
      api.payments.setPaid(id, period, { paid, amountMinorActual }),
    // Эндпоинт возвращает ОДНУ строку payment_states, а не список платежей,
    // поэтому кеш списка не подменяем — только инвалидируем и перезапрашиваем.
    onSuccess: () => invalidatePayments(qc),
  });
}

/* ------------------------------- categories ----------------------------- */

export function useCategories(includeArchived = false): UseQueryResult<CategoryDto[], ApiError> {
  return useQuery<CategoryDto[], ApiError>({
    queryKey: queryKeys.categories(includeArchived),
    queryFn: () => api.categories.list(includeArchived),
    // Справочник меняется редко — держим подольше, чипы должны появляться мгновенно.
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCategory(): UseMutationResult<CategoryDto, ApiError, CategoryCreateDto> {
  const qc = useQueryClient();
  return useMutation<CategoryDto, ApiError, CategoryCreateDto>({
    mutationFn: (body) => api.categories.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.categoriesAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.budgetsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
    },
  });
}

export function useUpdateCategory(): UseMutationResult<
  CategoryDto,
  ApiError,
  { id: number; patch: CategoryPatchDto }
> {
  const qc = useQueryClient();
  return useMutation<CategoryDto, ApiError, { id: number; patch: CategoryPatchDto }>({
    mutationFn: ({ id, patch }) => api.categories.patch(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.categoriesAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.budgetsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
    },
  });
}

/* -------------------------------- budgets ------------------------------- */

export function useBudgets(period: string): UseQueryResult<BudgetsResponseDto, ApiError> {
  return useQuery<BudgetsResponseDto, ApiError>({
    queryKey: queryKeys.budgets(period),
    queryFn: () => api.budgets.list(period),
  });
}

export function useUpdateBudget(
  period: string,
): UseMutationResult<BudgetDto, ApiError, { id: number } & BudgetPatchDto> {
  const qc = useQueryClient();
  // period в ключе не используется для подмены кеша: PATCH отдаёт одну строку,
  // а не весь BudgetsResponseDto — список перезапрашиваем инвалидацией.
  void period;
  return useMutation<BudgetDto, ApiError, { id: number } & BudgetPatchDto>({
    mutationFn: ({ id, limitMinor }) => api.budgets.patch(id, { limitMinor }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.budgetsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
    },
  });
}

/**
 * Upsert лимита на период. Нужен там, где строки budgets ещё нет:
 * новая категория или категория, заведённая после роллинга периода
 * (в списке она приходит с id: 0, и PATCH /api/budgets/0 вернёт 404).
 */
export function useUpsertBudget(
  period: string,
): UseMutationResult<BudgetDto, ApiError, { categoryId: number; limitMinor: number }> {
  const qc = useQueryClient();
  return useMutation<BudgetDto, ApiError, { categoryId: number; limitMinor: number }>({
    mutationFn: ({ categoryId, limitMinor }) => {
      const body: BudgetUpsertBody = { period, categoryId, limitMinor };
      return api.budgets.upsert(body);
    },
    // Ответ — ОДНА строка BudgetDto, а не BudgetsResponseDto: класть его
    // в queryKeys.budgets(period) нельзя, у экрана пропадёт data.items.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.budgetsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.overviewAll() });
    },
  });
}

/* ------------------------------ transactions ---------------------------- */

export function useTransactions(
  period: string,
  categoryId?: number,
): UseQueryResult<TransactionDto[], ApiError> {
  return useQuery<TransactionDto[], ApiError>({
    queryKey: queryKeys.transactions(period, categoryId),
    queryFn: () => api.transactions.list({ period, category: categoryId }),
  });
}

export function useCreateTransaction(): UseMutationResult<
  TransactionDto,
  ApiError,
  TransactionCreateDto
> {
  const qc = useQueryClient();
  return useMutation<TransactionDto, ApiError, TransactionCreateDto>({
    mutationFn: (body) => api.transactions.create(body),
    onSuccess: () => invalidateSpending(qc),
  });
}

export function useUpdateTransaction(): UseMutationResult<
  TransactionDto,
  ApiError,
  { id: number; patch: TransactionPatchDto }
> {
  const qc = useQueryClient();
  return useMutation<TransactionDto, ApiError, { id: number; patch: TransactionPatchDto }>({
    mutationFn: ({ id, patch }) => api.transactions.patch(id, patch),
    onSuccess: () => invalidateSpending(qc),
  });
}

export function useDeleteTransaction(): UseMutationResult<void, ApiError, number> {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api.transactions.remove(id),
    onSuccess: () => invalidateSpending(qc),
  });
}

/* --------------------------------- rates -------------------------------- */

export function useRate(on: string): UseQueryResult<RateDto, ApiError> {
  return useQuery<RateDto, ApiError>({
    queryKey: queryKeys.rate(on),
    queryFn: () => api.rates.get(on),
    // Курс дневной, перезапрашивать в течение сессии смысла нет.
    staleTime: 30 * 60 * 1000,
  });
}
